import {
  DECLARED_MONTHLY_KWH_MAX,
  DECLARED_MONTHLY_KWH_MIN,
  type HomeProfileSize,
} from '@rheora/enrollment-contract';

import {
  DEFAULT_REFERENCE_USAGE,
  type HomeType,
  type NeedsQuizResult,
  type ReferenceUsage,
  type UsageSize,
} from '../schemas/shop.schema';
import { tierForMonthly } from './consumption';

/**
 * Maps a home-size answer to one of the three PUCT EFL reference usages. This is
 * the ONLY thing the needs-quiz does to pricing in E-SF02: it chooses WHICH
 * already-computed, compliant average price the shelf headlines. It never
 * fabricates a number and never interpolates between reference usages — the
 * per-premise "your estimated bill" personalisation is E-SF04, not this.
 */
export function usageSizeToReferenceKwh(size: UsageSize): ReferenceUsage {
  switch (size) {
    case 'small':
      return 500;
    case 'medium':
      return 1000;
    case 'large':
      return 2000;
  }
}

/**
 * The reference usage the shelf should headline. With a quiz answer it follows
 * the home size; without one it falls back to the standard 1000 kWh EFL midpoint.
 * `homeType` is captured by the quiz for the funnel but does not affect price in
 * E-SF02 (it feeds E-SF04 personalisation later).
 */
export function referenceUsageFor(quiz: NeedsQuizResult | undefined): ReferenceUsage {
  if (quiz === undefined) return DEFAULT_REFERENCE_USAGE;
  return usageSizeToReferenceKwh(quiz.usageSize);
}

/**
 * Maps the home TYPE to a reference usage for the ZIP-first entry (E-SF10 gate):
 * an apartment uses less, a house more. This is the consumption *estimate* the
 * shelf headlines when the shopper hasn't given a per-premise usage — the real
 * per-address figure is E-SF04 personalisation. Never fabricates a number; only
 * selects which compliant reference-usage price to show.
 */
export function homeTypeToReferenceKwh(homeType: HomeType): ReferenceUsage {
  switch (homeType) {
    case 'apartment':
      return 500;
    case 'condo':
      return 1000;
    case 'house':
      return 2000;
  }
}

/**
 * The chosen monthly kWh → the home-size PROFILE the BFF prices the estimate and
 * the PUCT §25.478 deposit against (QA-2026-08-05 BLOCKER 1).
 *
 * ── WHY THE TIER BOUNDARIES AND NOT A NEAREST-NEIGHBOUR RULE ───────────────
 * `consumption.ts` already defines what small/medium/large mean here
 * (0<m≤500 / 500<m≤1000 / 1000<m≤2000), and each tier's REPRESENTATIVE monthly
 * kWh is its upper bound — exactly the three PUCT EFL reference usages the BFF's
 * `HOME_PROFILE_KWH` maps to. So a tier selection maps LOSSLESSLY, and a custom
 * figure maps by the same published rule the shelf already uses to headline it.
 * Inventing a second rule here would let the shelf and the estimate disagree
 * about which home the shopper lives in.
 *
 * Above 2000 kWh/mo there is no larger profile to name, so `large` is the honest
 * answer — it is the largest usage basis the BFF will price a fallback at.
 *
 * @returns the profile to record, or `null` for a non-positive figure — in which
 *   case NOTHING is sent and the BFF applies its own documented medium default,
 *   labelled as such on screen. We never post a fabricated size.
 */
export function homeProfileForMonthlyKwh(monthlyKwh: number): HomeProfileSize | null {
  if (!Number.isFinite(monthlyKwh) || monthlyKwh <= 0) return null;

  const tier = tierForMonthly(monthlyKwh);
  if (tier === null) return 'large';

  switch (tier.id) {
    case 'low':
      return 'small';
    case 'medium':
      return 'medium';
    case 'high':
      return 'large';
  }
}

/**
 * Groups a reference usage for display (e.g. 1000 → "1,000"). Kept as a pure
 * formatter so the usage toggle can label its segments without embedding
 * grouping logic in the component.
 */
export function formatUsageKwh(usage: ReferenceUsage): string {
  return usage.toLocaleString('en-US');
}

/**
 * The shopper's chosen monthly kWh as a DECLARED usage the BFF will accept, or
 * `null` when there is nothing usable to declare (E-SF19 P0a).
 *
 * ── WHY A GATE RATHER THAN A CLAMP ─────────────────────────────────────────
 * The contract bounds a declared figure to [50, 10 000] kWh/mo, and the BFF
 * answers `400` outside that range rather than quietly answering about a
 * different number. So a value the server would refuse must not be SENT: sending
 * it would turn a shopper's typo into a dead-ended estimate screen, when the
 * honest fallback — say nothing, and let the server use the labelled home-size
 * profile — is already correct and already disclosed on screen.
 *
 * A figure BELOW the floor is the interesting case. `consumption.parseCustomKwh`
 * accepts any positive integer, so 1 kWh/mo is a valid Custom entry as far as the
 * form is concerned. It is not a plausible occupied-residence figure, and it is
 * the one direction a customer benefits from misstating (a lower projected bill
 * is a lower §25.478 deposit ceiling). Withholding it costs the shopper nothing
 * they can see and keeps the deposit basis defensible.
 *
 * ⚠️ NOT ROUNDED, NOT CORRECTED. Whatever we send is what the customer will be
 * shown a price for, so it is their figure or it is nothing.
 */
export function declaredMonthlyKwhFor(monthlyKwh: number | undefined): number | null {
  if (monthlyKwh === undefined || !Number.isInteger(monthlyKwh)) return null;
  if (monthlyKwh < DECLARED_MONTHLY_KWH_MIN || monthlyKwh > DECLARED_MONTHLY_KWH_MAX) return null;
  return monthlyKwh;
}
